/**
 * Decoders List
 * View and test payload decoders
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchDecoders, testDecoder } from './api';
import type { Decoder, DecoderTestResult } from './types';

export default function DecodersList() {
  const { data: decoders = [], isLoading, error } = useQuery({
    queryKey: ['iot', 'decoders'],
    queryFn: fetchDecoders,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load decoders: {String(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Decoders</h1>
        <p className="text-gray-600 mt-1">{decoders.length} payload decoders</p>
      </div>

      {/* Decoders List */}
      {decoders.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Decoders Found</h3>
          <p className="text-gray-600">Use the Agent chat to create decoders</p>
        </div>
      ) : (
        <div className="space-y-4">
          {decoders.map((decoder) => (
            <DecoderCard key={decoder.id} decoder={decoder} />
          ))}
        </div>
      )}
    </div>
  );
}

function DecoderCard({ decoder }: { decoder: Decoder }) {
  const [testPayload, setTestPayload] = useState('');
  const [testResult, setTestResult] = useState<DecoderTestResult | null>(null);

  const testMutation = useMutation({
    mutationFn: (payload: string) => testDecoder(decoder.id, payload, decoder.expects.payloadType),
    onSuccess: (result) => setTestResult(result),
  });

  const handleTest = () => {
    if (!testPayload.trim()) return;
    setTestResult(null);
    testMutation.mutate(testPayload);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{decoder.id}</h3>
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
            <span className="flex items-center">
              <span className="font-medium mr-1">Entity:</span>
              {decoder.emits.entityType}
            </span>
            <span className="flex items-center">
              <span className="font-medium mr-1">Format:</span>
              {decoder.expects.payloadType.toUpperCase()}
            </span>
            <span className="flex items-center">
              <span className="font-medium mr-1">Version:</span>
              {decoder.version}
            </span>
          </div>
        </div>
      </div>

      {/* Test Section */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Payload ({decoder.expects.payloadType.toUpperCase()})
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={testPayload}
            onChange={(e) => setTestPayload(e.target.value)}
            placeholder={`Enter ${decoder.expects.payloadType} payload...`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          />
          <button
            onClick={handleTest}
            disabled={testMutation.isPending || !testPayload.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testMutation.isPending ? 'Testing...' : 'Test'}
          </button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-4 p-4 rounded-md ${testResult.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {testResult.ok ? (
              <div>
                <p className="text-sm font-medium text-green-800 mb-2">✓ Decode Successful</p>
                <pre className="text-xs bg-white p-3 rounded border border-green-200 overflow-x-auto">
                  {JSON.stringify(testResult.decoded, null, 2)}
                </pre>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-red-800 mb-2">✗ Decode Failed</p>
                {testResult.errors && (
                  <div className="text-xs text-red-700 space-y-1">
                    {testResult.errors.map((err, idx) => (
                      <div key={idx}>• {err}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {testMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">Error: {String(testMutation.error)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

